import {AccountExtraFee, AccountExtraFeeInput, AccountExtraFeeType, Maybe} from 'api/generated';

import {AccountExtraFeeRegion} from 'api/generated';
import styled from 'styled-components';

// Formatters
const number = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0
});

const currency = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'EUR'
});

export const percent = new Intl.NumberFormat(undefined, {
  style: 'percent',
  maximumFractionDigits: 2
});

const bigNumber = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0
});

export const InputAddon = styled.div`
  width: 25px;
`;

/**
 * Checks if the given fee type is regional (applies to specific regions/time periods)
 * @param value - The account extra fee type to check
 * @returns True if the fee type is DAILY, ONE_TIME, or MONTHLY
 * @example
 * checkIfRegional(AccountExtraFeeType.DAILY) // returns true
 * checkIfRegional(AccountExtraFeeType.PERCENT) // returns false
 */
export const checkIfRegional = (value: AccountExtraFeeType) => {
  return [
    AccountExtraFeeType.DAILY,
    AccountExtraFeeType.ONE_TIME,
    AccountExtraFeeType.MONTHLY
  ].includes(value);
};

/**
 * Checks if the given region applies to all transactions
 * @param value - The account extra fee region to check
 * @returns True if the region is ALL (applies to all transactions)
 * @example
 * checkIfRegionAll(AccountExtraFeeRegion.ALL) // returns true
 * checkIfRegionAll(AccountExtraFeeRegion.DOMESTIC) // returns false
 */
export const checkIfRegionAll = (value: AccountExtraFeeRegion) => {
  return [AccountExtraFeeRegion.ALL].includes(value);
};

/**
 * Formats a flat fee amount from cents to EUR currency format
 * @param amount - The amount in cents (can be null or undefined)
 * @returns Formatted currency string in EUR
 * @example
 * formatFlatFee(1500) // returns "€15.00"
 * formatFlatFee(null) // returns "€0.00"
 */
export const formatFlatFee = (amount: number | null | undefined = 0) => {
  return currency.format((amount ?? 0) / 100);
};

/**
 * Formats a fee percentage from basis points to percentage format
 * @param value - The percentage value in basis points (can be null or undefined)
 * @returns Formatted percentage string
 * @example
 * formatFee(250) // returns "2.50%"
 * formatFee(null) // returns "0.00%"
 */
export const formatFee = (value: number | null | undefined = 0) => {
  return percent.format((value ?? 0) / 100);
};

const regionLabelMap: Record<AccountExtraFeeRegion | AccountExtraFeeType, string> = {
  [AccountExtraFeeType.PERCENT]: 'percent',
  [AccountExtraFeeType.MONEY]: 'money',
  [AccountExtraFeeType.MONTHLY]: 'per month',
  [AccountExtraFeeType.DAILY]: 'per day',
  [AccountExtraFeeType.ONE_TIME]: 'one time',
  [AccountExtraFeeRegion.DOMESTIC]: 'of successful EU credit cards transactions',
  [AccountExtraFeeRegion.NON_DOMESTIC]: 'of successful non EU credit cards transactions',
  [AccountExtraFeeRegion.ALL]: 'of successful transactions'
};

export const accountInvoicesVatOptions: Record<string, number> = {
  EXEMPTED: 0,
  DEFAULT: 21
};

/**
 * Formats the region description based on fee type and region
 * @param type - The account extra fee type
 * @param region - The account extra fee region
 * @returns Human-readable description of the fee scope
 * @example
 * formatRegion(AccountExtraFeeType.DAILY, AccountExtraFeeRegion.ALL) // returns "per day"
 * formatRegion(AccountExtraFeeType.PERCENT, AccountExtraFeeRegion.DOMESTIC) // returns "of successful EU credit cards transactions"
 * formatRegion(AccountExtraFeeType.MONEY, AccountExtraFeeRegion.ALL) // returns "per successful transaction"
 */
const formatRegion = (type: AccountExtraFeeType, region: AccountExtraFeeRegion) => {
  // Special case: when type is MONEY and region is ALL, return singular form
  if (type === AccountExtraFeeType.MONEY && region === AccountExtraFeeRegion.ALL) {
    return 'per successful transaction';
  }

  return regionLabelMap[checkIfRegional(type) ? type : region] ?? '';
};

/**
 * Formats a large amount from cents to EUR currency format without decimal places
 * @param amount - The amount in cents (can be null or undefined)
 * @returns Formatted currency string in EUR without decimals
 * @example
 * formatBigNumber(150000) // returns "€1,500"
 * formatBigNumber(null) // returns "€0"
 */
export const formatBigNumber = (amount: number | null | undefined = 0) => {
  return bigNumber.format((amount ?? 0) / 100);
};

/**
 * Formats an account extra fee into a human-readable string representation
 * @param fee - The account extra fee object (can be null or undefined)
 * @returns Formatted fee string with amount, region, payment methods, and master account badge, or null if no fee
 * @example
 * formatExtraFee({
 *   amount: 250,
 *   type: AccountExtraFeeType.PERCENT,
 *   region: AccountExtraFeeRegion.ALL,
 *   masterAccountFee: true,
 *   paymentMethods: ['CARD']
 * }) // returns "2.50% of successful transactions [CARD] [MA]"
 */
export const formatExtraFee = (fee?: Maybe<AccountExtraFee>) => {
  if (!fee) return null;

  const {amount, type, region = AccountExtraFeeRegion.ALL, masterAccountFee} = fee;
  const masterAccountFeeBadge = masterAccountFee ? '[MA]' : '';
  const formattedRegion = formatRegion(type, region!);
  switch (type) {
    case AccountExtraFeeType.DAILY:
    case AccountExtraFeeType.MONTHLY:
    case AccountExtraFeeType.ONE_TIME: {
      return `${formatFlatFee(amount)} ${formattedRegion} ${masterAccountFeeBadge}`;
    }
    case AccountExtraFeeType.PERCENT: {
      const paymentMethods = fee.paymentMethods ? `[${fee.paymentMethods?.join(',')}]` : '';
      return `${formatFee(amount)} ${formattedRegion} ${paymentMethods} ${masterAccountFeeBadge}`;
    }
    case AccountExtraFeeType.MONEY: {
      const paymentMethods = fee.paymentMethods ? `[${fee.paymentMethods?.join(',')}]` : '';
      return `${formatFlatFee(
        amount
      )} ${formattedRegion} ${paymentMethods} ${masterAccountFeeBadge}`;
    }
  }
  return amount;
};

/**
 * Input properties for handling large amount inputs with EUR formatting
 * Provides formatter and parser functions for converting between display and storage formats
 * @example
 * // Usage in an input component:
 * <InputNumber {...bigAmountInputProps} />
 */
export const bigAmountInputProps = {
  formatter: (value: string | number | undefined) => {
    if (value === undefined) return '';
    const amount = parseInt(value as string);
    if (Number.isNaN(amount)) return value.toString();
    return number.format(amount / 100);
  },
  parser: (value: string | undefined) => {
    if (value === undefined) return '';
    if (value.endsWith('.')) return value;
    const amount = parseInt(value.replace(/\$\s?|(,*)/g, ''));
    if (Number.isNaN(amount)) return value;
    return Math.floor(amount * 100);
  },
  addonAfter: 'EUR'
};

/**
 * Input properties for handling amount inputs with EUR formatting and decimal support
 * Provides formatter and parser functions with user typing detection
 * @example
 * // Usage in an input component:
 * <InputNumber {...amountInputProps} />
 */
export const amountInputProps = {
  formatter: (
    value: string | number | undefined,
    info: {
      userTyping: boolean;
      input: string;
    }
  ): string => {
    if (info.userTyping) return info.input;
    const amount = parseInt(value as string);
    if (Number.isNaN(amount)) return value as string;
    return (amount / 100).toString();
  },
  parser: (value: string | undefined) => {
    if (value === undefined) return '';
    if (value.endsWith('.')) return value;
    const amount = parseFloat(value);
    if (Number.isNaN(amount)) return value;
    return Math.floor(amount * 100);
  },
  addonAfter: 'EUR'
};

/**
 * Determines if a fee supports payment method specifications
 * @param fee - The account extra fee object to check
 * @returns True if the fee applies to all regions and is either PERCENT or MONEY type
 * @example
 * isFeeWithPaymentMethods({
 *   type: AccountExtraFeeType.PERCENT,
 *   region: AccountExtraFeeRegion.ALL
 * }) // returns true
 *
 * isFeeWithPaymentMethods({
 *   type: AccountExtraFeeType.DAILY,
 *   region: AccountExtraFeeRegion.DOMESTIC
 * }) // returns false
 */
export const isFeeWithPaymentMethods = (fee?: AccountExtraFee | AccountExtraFeeInput) => {
  if (!fee) return false;
  return (
    fee.region === AccountExtraFeeRegion.ALL &&
    (fee.type === AccountExtraFeeType.PERCENT || fee.type === AccountExtraFeeType.MONEY)
  );
};
